/**
 * Gift matching pool types
 *
 * Shapes match the API's serialised models, with enums in their camelCase wire form.
 */

export const conditionKey = {
  campaignTotal: 'campaignTotal',
  dateTime: 'dateTime',
  donationAmount: 'donationAmount',
  donationCount: 'donationCount',
  giftMatchingTotal: 'giftMatchingTotal',
  pageCollection: 'pageCollection',
  pageFundraiser: 'pageFundraiser',
  pageTeam: 'pageTeam'
} as const;
export type ConditionKey = (typeof conditionKey)[keyof typeof conditionKey];

export const conditionOperator = {
  between: 'between',
  equalTo: 'equalTo',
  greaterOrEqualThan: 'greaterOrEqualThan',
  lesserOrEqualThan: 'lesserOrEqualThan'
} as const;
export type ConditionOperator =
  (typeof conditionOperator)[keyof typeof conditionOperator];

export interface GiftMatchingCondition {
  id?: number;
  key: ConditionKey | string;
  operator: ConditionOperator | string;
  value: (string | number | null)[];
}

export interface GiftMatchingDonation {
  from: string | null;
  message: string | null;
}

export interface AccountBasic {
  email: string | null;
  firstName: string | null;
  id: number;
  lastName: string | null;
}

export interface PoolCampaign {
  campaignId: number;
  campaignName: string | null;
  currency: string | null;
  donationMatchedCount: number;
  donationMatchedTotalAmount: number;
  giftMatchingId: number;
  organizationId: number;
  organizationName: string | null;
}

export interface GiftMatchingPool {
  campaigns: PoolCampaign[];
  conditions: GiftMatchingCondition[];
  createdAt: string;
  createdBy: AccountBasic | null;
  currency: string | null;
  donation: GiftMatchingDonation | null;
  donationMatchedCount: number;
  donationMatchedTotalAmount: number;
  enabled: boolean;
  id: number;
  lastUpdatedAt: string;
  lastUpdatedBy: AccountBasic | null;
  mainImagePath: string | null;
  multiplier: number;
  name: string;
}

export interface CampaignGiftMatch {
  conditions: GiftMatchingCondition[] | null;
  donation: GiftMatchingDonation | null;
  donationMatchedCount: number;
  donationMatchedTotalAmount: number;
  enabled: boolean;
  id: number;
  isPoolEntry: boolean;
  mainImagePath: string | null;
  multiplier: number;
  name: string;
  poolId: number | null;
}

export interface CampaignSearchResult {
  id: number;
  name: string;
  organization?: { country?: string | null; name?: string | null } | null;
  organizationId?: number | null;
  status?: string | null;
  url?: string | null;
  urlPath?: string | null;
}

export interface CampaignSummary {
  campaignId: number;
  campaignName: string | null;
  currency: string | null;
  giftMatching: CampaignGiftMatch[];
  organizationId: number;
  organizationName: string | null;
}

export interface PoolSettings {
  conditions: GiftMatchingCondition[];
  donation: GiftMatchingDonation;
  enabled: boolean;
  mainImagePath: string | null;
  multiplier: number;
  name: string;
}

/** A pool takes its settings and conditions from the source gift match. */
export interface PoolCreation {
  campaignIds: number[];
  sourceGiftMatchingId: number;
}

export interface ConditionToggle {
  enabled: boolean;
  value: string;
}

export interface DonationAmountCondition {
  enabled: boolean;
  from: string;
  operator: ConditionOperator;
  to: string;
}

export interface ConditionFormValues {
  campaignTotal: ConditionToggle;
  donationAmount: DonationAmountCondition;
  donationCount: ConditionToggle;
  endsAt: ConditionToggle;
  giftMatchingTotal: ConditionToggle;
  startsAt: ConditionToggle;
}

export interface PoolFormValues {
  conditions: ConditionFormValues;
  donationFrom: string;
  donationMessage: string;
  enabled: boolean;
  mainImagePath: string | null;
  multiplier: number;
  name: string;
}
